import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import localimgpath from "../../js/localimgpath";
import axios from "axios";
import {
  useTalentMutation,
  useUpdateTalent,
} from "../../actions/authActions";
import { useNavigate } from "react-router-dom";
import textWeight from "../../utils/textWeight";

// Helper function to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
  
  const baseURL = process.env.REACT_APP_BASE_URL;


async function getCroppedImg(imageSrc, pixelCrop, cropType = "default") {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true, alpha: true });

  // 🧠 Determine export size
  let exportWidth = pixelCrop.width;
  let exportHeight = pixelCrop.height;

  // ✅ Force banner crop to 1920x1080 (16:9)
  if (cropType === "banner") {
    exportWidth = 1920;
    exportHeight = 600;  // <- Changed from 1080 to 600
  }

  canvas.width = exportWidth;
  canvas.height = exportHeight;

  // 🟢 Clear and draw cropped area at correct scale
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    exportWidth,
    exportHeight
  );

  // 🟢 Preserve PNG transparency if applicable
  const mimeType = imageSrc.startsWith("data:image/png")
    ? "image/png"
    : "image/jpeg";

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, 0.95); // 95% quality for JPEG
  });
}



export default () => {
  const { TextWeight } = textWeight();
  const [galleryList, setGalleryList] = useState([]);
  
  // Helper function to get cropped image (auto-detects PNG vs JPG and keeps transparency)
  const accessToken =
  useSelector((state) => state.app?.access_token?.access_token) ||
  localStorage.getItem("access_token");

  const userData = useSelector((state) => state.app.userData.userData);
  const {
    data: talentData,
    isError,
    error,
    refetch,
  } = useUpdateTalent(userData?.talent?.id);
  const [submittedCount, setSubmittedCount] = useState({ approved: 0, total: 0 });
  useEffect(() => {
    if (talentData?.galleries) {
      setGalleryList(talentData.galleries);
    }

    // ✅ Count approved vs total uploaded
    if (talentData?.all_galleries?.length) {
      const approved = talentData.all_galleries.filter(
        (g) => g.status?.toLowerCase() === "approved"
      ).length;
      const total = talentData.all_galleries.length;

      setSubmittedCount({ approved, total });
    } else if (talentData?.galleries?.length) {
      // Fallback: only approved ones available
      setSubmittedCount({
        approved: talentData.galleries.length,
        total: talentData.galleries.length,
      });
    } else {
      setSubmittedCount({ approved: 0, total: 0 });
    }

    // ✅ Banner logic
    if (talentData?.banner) {
      const cacheBustedUrl = `${localimgpath?.talentBanner}/${talentData.banner}?v=${Date.now()}&r=${Math.random()}`;
      setTalentBanner(cacheBustedUrl);
    } else {
      setTalentBanner(null);
    }
  }, [talentData]);

  // ✅ Fetch all galleries (approved + pending)
  useEffect(() => {
    const fetchAllGalleries = async () => {
      if (!talentData?.id) return;

      try {
        const res = await axios.get(
          `${baseURL}/api/v2/talent/${talentData.id}/galleries?status=all`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        const galleries = res.data?.data || [];
        const approved = galleries.filter(
          (g) => g.status?.toLowerCase() === "approved"
        ).length;
        const total = galleries.length;

        setSubmittedCount({ approved, total });
      } catch (err) {
        console.error("Failed to fetch all galleries:", err);

        // fallback if API not available
        setSubmittedCount({
          approved: talentData?.galleries?.length || 0,
          total: talentData?.galleries?.length || 0,
        });
      }
    };

    fetchAllGalleries();
  }, [talentData]);



  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingToapi, setLoadingToapi] = useState(false);
  const [loading, setLoading] = useState({
    HeroImage: false,
    BannerImage: false,
    GalleryImage: false,
    Project: false,
  });

  // Gallery modal state
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Cropper state - only for hero and banner
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperType, setCropperType] = useState(null); // 'hero' or 'banner'
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Preview state for selected images BEFORE upload
  const [selectedHeroImage, setSelectedHeroImage] = useState(null);
  const [selectedBannerImage, setSelectedBannerImage] = useState(null);
  const [selectedGalleryImages, setSelectedGalleryImages] = useState([]);
  const [talentBanner, setTalentBanner] = useState(null);


  // Use the regular mutation
  const mutation = useTalentMutation(
    talentData?.id,
    dispatch,
    setLoadingToapi,
    navigate
  );

  // Cropper callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Show cropper when hero image is selected
  const handleHeroImageSelect = (e) => {
    const { files: selectedFiles } = e.target;
    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      
      // Validate file size (2MB = 2 * 1024 * 1024 bytes)
      if (file.size > 2 * 1024 * 1024) {
        alert("Image size must be less than 2MB");
        e.target.value = ''; // Reset file input
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropperType('hero');
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Show cropper when banner image is selected
  const handleBannerImageSelect = (e) => {
    const { files: selectedFiles } = e.target;
    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      
      // Validate file size (2MB = 2 * 1024 * 1024 bytes)
      if (file.size > 2 * 1024 * 1024) {
        alert("Image size must be less than 2MB");
        e.target.value = ''; // Reset file input
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropperType('banner');
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle gallery images - NO CROPPER, just validation and preview
  const handleGalleryImagesSelect = (e) => {
    const { files: selectedFiles } = e.target;

    if (selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);
      
      // Check max 10 images limit
      const currentCount = selectedGalleryImages.length;
      const totalCount = currentCount + fileArray.length;
      
      if (totalCount > 10) {
        alert(`You can only upload a maximum of 10 images. You currently have ${currentCount} image(s) selected. You can add ${10 - currentCount} more.`);
        e.target.value = ''; // Reset file input
        return;
      }
      
      // Validate file sizes
      const invalidFiles = fileArray.filter(file => file.size > 2 * 1024 * 1024);
      if (invalidFiles.length > 0) {
        alert(`${invalidFiles.length} image(s) exceed the 2MB size limit. Please select smaller images.`);
        e.target.value = ''; // Reset file input
        return;
      }
      
      // Create previews for valid files
      const previews = fileArray.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      
      setSelectedGalleryImages(prev => [...prev, ...previews]);
      e.target.value = ''; // Reset file input so user can add more
    }
  };

  // Apply crop and save cropped image (only for hero and banner)
  const handleCropSave = async () => {
    try {
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels, cropperType);
      
      if (cropperType === 'hero') {
        const croppedFile = new File([croppedBlob], "hero-image.png", {
          type: "image/png",
        });
        const previewUrl = URL.createObjectURL(croppedBlob);
        setSelectedHeroImage({ file: croppedFile, preview: previewUrl });
        
        // Close cropper
        setCropperOpen(false);
        setImageToCrop(null);
        setCropperType(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      } 
      else if (cropperType === 'banner') {
        const croppedFile = new File([croppedBlob], "banner-image.png", {
          type: "image/png",
        });
        const previewUrl = URL.createObjectURL(croppedBlob);
        setSelectedBannerImage({ file: croppedFile, preview: previewUrl });
        
        // Close cropper
        setCropperOpen(false);
        setImageToCrop(null);
        setCropperType(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      }
    } catch (error) {
      console.error("Error cropping image:", error);
      alert("Failed to crop image. Please try again.");
    }
  };

  // Cancel crop
  const handleCropCancel = () => {
    setCropperOpen(false);
    setImageToCrop(null);
    setCropperType(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Upload hero image when Update button is clicked
  const handleHeroImageUpload = async () => {
    if (!selectedHeroImage) {
      alert("Please select an image first");
      return;
    }

    if (!talentData?.stage_name || !talentData?.age || !talentData?.gender?.gender_id) {
      alert("Missing required talent data. Please complete your profile first.");
      return;
    }

    setLoading((prev) => ({ ...prev, HeroImage: true }));
    
    try {
      const apiData = new FormData();
      apiData.append("stage_name", talentData.stage_name);
      apiData.append("age", String(talentData.age));
      apiData.append("gender", String(talentData.gender.gender_id));
      apiData.append("hero_image", selectedHeroImage.file);
      apiData.append("_method", "PUT");

      console.log("Uploading hero image...");
      await mutation.mutateAsync(apiData);
      
      // Success - clear preview and refetch
      if (selectedHeroImage?.preview) {
        URL.revokeObjectURL(selectedHeroImage.preview);
      }
      setSelectedHeroImage(null);
      await refetch();
      alert("Profile image updated successfully!");
      
    } catch (error) {
      console.error("Hero image upload error:", error);
      alert("Failed to upload profile image: " + (error.message || "Unknown error"));
    } finally {
      setLoading((prev) => ({ ...prev, HeroImage: false }));
    }
  };

  // Upload banner image when Update button is clicked
  const handleBannerImageUpload = async () => {
    if (!selectedBannerImage) {
      alert("Please select an image first");
      return;
    }

    if (!talentData?.stage_name || !talentData?.age || !talentData?.gender?.gender_id) {
      alert("Missing required talent data. Please complete your profile first.");
      return;
    }

    setLoading((prev) => ({ ...prev, BannerImage: true }));

    try {
      const apiData = new FormData();
      apiData.append("stage_name", talentData.stage_name);
      apiData.append("age", String(talentData.age));
      apiData.append("gender", String(talentData.gender.gender_id));
      apiData.append("banner", selectedBannerImage.file);
      apiData.append("_method", "PUT");

      console.log("Uploading banner image...");
      await mutation.mutateAsync(apiData);

      if (selectedBannerImage?.preview) {
        URL.revokeObjectURL(selectedBannerImage.preview);
      }
      setSelectedBannerImage(null);

      // Refetch to get the latest banner - useEffect will handle updating talentBanner
      await refetch();
      
      alert("Banner image updated successfully!");
    } catch (error) {
      console.error("Banner upload error:", error);
      alert("Failed to upload banner: " + (error.message || "Unknown error"));
    } finally {
      setLoading((prev) => ({ ...prev, BannerImage: false }));
    }
  };

  // Upload gallery images when Update button is clicked
  const handleGalleryImagesUpload = async () => {
    if (selectedGalleryImages.length === 0) {
      alert("Please select at least one image first");
      return;
    }

    if (!talentData?.id) {
      alert("Missing Talent ID");
      return;
    }

    setLoading((prev) => ({ ...prev, GalleryImage: true }));

    try {
      const apiData = new FormData();
      
      // Append each gallery image with the same key (Laravel format for multiple files)
      selectedGalleryImages.forEach((imageObj) => {
        apiData.append('gallery[]', imageObj.file);
      });

      console.log(`Uploading ${selectedGalleryImages.length} gallery images...`);
      
      const response = await axios.post(
        `${baseURL}/api/v2/talent/${talentData.id}/galleries`,
        apiData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Gallery upload response:", response.data);

      // Clean up previews
      selectedGalleryImages.forEach((imageObj) => {
        if (imageObj?.preview) {
          URL.revokeObjectURL(imageObj.preview);
        }
      });
      setSelectedGalleryImages([]);

      // Refetch to get the updated gallery list
      await refetch();
      
      alert("Gallery images uploaded successfully!");
    } catch (error) {
      console.error("Gallery upload error:", error);
      alert("Failed to upload gallery images: " + (error.response?.data?.message || error.message || "Unknown error"));
    } finally {
      setLoading((prev) => ({ ...prev, GalleryImage: false }));
    }
  };





  // Remove selected image before upload
  const removeSelectedHeroImage = () => {
    if (selectedHeroImage?.preview) {
      URL.revokeObjectURL(selectedHeroImage.preview);
    }
    setSelectedHeroImage(null);
  };

  // Delete gallery image
  const handleDeleteGalleryImage = async (galleryId) => {
    if (!talentData?.id) {
      alert("Missing Talent ID");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const response = await axios.post(
        `${baseURL}/api/v2/talent/${talentData.id}/gallery/${galleryId}/delete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("Deleted gallery image:", response.data);
      await refetch();
      alert("Image deleted successfully!");
    } catch (error) {
      console.error("Failed to delete image:", error);
      alert(
        "Failed to delete image: " +
          (error.response?.data?.message || error.message)
      );
    }
  };



  const removeSelectedBannerImage = () => {
    if (selectedBannerImage?.preview) {
      URL.revokeObjectURL(selectedBannerImage.preview);
    }
    setSelectedBannerImage(null);
  };

  const removeSelectedGalleryImage = (index) => {
    const newImages = [...selectedGalleryImages];
    if (newImages[index]?.preview) {
      URL.revokeObjectURL(newImages[index].preview);
    }
    newImages.splice(index, 1);
    setSelectedGalleryImages(newImages);
  };

  // Get aspect ratio based on cropper type
  const getCropperAspectRatio = () => {
    switch (cropperType) {
      case 'hero':
        return 430 / 575;
      case 'banner':
        return 1920 / 600;
      default:
        return 1;
    }
  };

  // Get cropper title based on type
  const getCropperTitle = () => {
    switch (cropperType) {
      case 'hero':
        return 'Crop Profile Image (430×575 px)';
      case 'banner':
        return 'Crop Banner Image (1920×600 px)';
      default:
        return 'Crop Image';
    }
  };

  // Gallery images for modal
  const galleryImages = galleryList.map((item) => ({
    id: item?.id, // ✅ Add ID so delete function works
    original: `${localimgpath?.talentGallery}/${item?.image}`,
    thumbnail: `${localimgpath?.talentGallery}/${item?.image}`,
    originalAlt: item?.title || "Gallery Image",
    description: item?.title || "",
  }));

  const openGallery = (index) => {
    setSelectedIndex(index);
    setIsOpen(true);
  };

  const closeGallery = () => setIsOpen(false);

  return {
    TextWeight,
    loading,
    talentData,
    galleryImages,
    isOpen,
    selectedIndex,
    isError, // ✅ Return error state
    error,   // ✅ Return error object
    
    // Cropper (only for hero and banner)
    cropperOpen,
    cropperType,
    imageToCrop,
    crop,
    zoom,
    setCrop,
    setZoom,
    onCropComplete,
    handleCropSave,
    handleCropCancel,
    getCropperAspectRatio,
    getCropperTitle,
    
    // Hero image
    selectedHeroImage,
    handleHeroImageSelect,
    handleHeroImageUpload,
    removeSelectedHeroImage,
    
    // Banner image
    selectedBannerImage,
    handleBannerImageSelect,
    handleBannerImageUpload,
    removeSelectedBannerImage,
    
    // Gallery images (no cropper)
    selectedGalleryImages,
    handleGalleryImagesSelect,
    handleGalleryImagesUpload,
    removeSelectedGalleryImage,
    handleDeleteGalleryImage,
    openGallery,
    closeGallery,
    navigate,
    localimgpath,
    talentBanner,
    submittedCount,
  };
};